import { Autocomplete, FormControl, TextField } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getEventTypes } from '../../../../fetch/calendar';
import { alphaSort } from '../../../../helpers';
import { IDropdownResponse } from '../../../../models';

interface IEventTypeDropdown {
  value: any[];
  handleChange: (val: string | number) => void;
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  inputLabel?: string;
  isEnabled?: boolean;
  name?: string;
  handleClear?: () => void;
}

export const EventDropdown: FC<IEventTypeDropdown> = ({
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  name,
  isEnabled = true,
  handleClear
}) => {
  const { isLoading: isLoadingEventTypes, data: eventTypes } = useQuery<IDropdownResponse[], Error>(
    ['event-types-filters'],
    () => getEventTypes(),
    {
      notifyOnChangeProps: 'tracked',
      onSuccess: d => setOptions?.([...d, { value: '', description: 'All', shorthand: 'all' }]),
      enabled: isEnabled
    }
  );
  const events = useMemo(
    () =>
      eventTypes?.map(r => ({
        ...r,
        fullName: `${r.description}`
      })) ?? [],
    [eventTypes]
  );

  const [inputValue, setInputValue] = useState('');

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired}>
      <Autocomplete
        id="event-type"
        multiple
        value={value}
        onChange={(event, newValue: any) => {
          return handleChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        options={alphaSort(events, 'description')}
        disabled={isDisabled || isLoadingEventTypes}
        getOptionLabel={(option: IDropdownResponse) => option?.description ?? option?.shorthand}
        filterSelectedOptions
        renderOption={(props, option: IDropdownResponse) => {
          return (
            <li {...props} key={`${option?.value}`}>
              {option.description}
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            disabled={isDisabled || isLoadingEventTypes}
            label={isLoadingEventTypes ? 'Loading...' : 'Event Type'}
            size="small"
          />
        )}
      />
    </FormControl>
  );
};
